import * as React from "react";
import Link from "next/link";
import { BriefcaseBusiness, CalendarDays, Clock3, Facebook, Linkedin, MapPin, Share2, X } from "lucide-react";

import type { PublicJob } from "../../lib/job-board/types";
import { jobUrl } from "../../lib/job-board/url";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ApplicationForm from "./ApplicationForm";

function range(value: { min?: number; max?: number }, suffix = "") {
  const { min, max } = value;
  if (min === undefined && max === undefined) return null;
  const label = min !== undefined && max !== undefined ? `${min}–${max}` : min !== undefined ? `${min}+` : `Up to ${max}`;
  return `${label}${suffix}`;
}

function employmentLabel(jobType: string): string {
  return jobType.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function postedLabel(date: string | null): string | null {
  if (!date) return null;
  const time = new Date(date).getTime();
  if (!Number.isFinite(time)) return null;
  const hours = Math.max(0, Math.floor((Date.now() - time) / 3_600_000));
  if (hours < 1) return "Posted just now";
  if (hours < 24) return `Posted ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `Posted ${days} day${days === 1 ? "" : "s"} ago`;
}

function descriptionText(value: string): string {
  return value
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/?(?:p|div|li|h[1-6]|tr)[^>]*>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function RelatedJob({ job }: { job: PublicJob }) {
  const experience = range(job.experience, " years");
  return (
    <li>
      <Link href={jobUrl(job.title, job.id)}>{job.title}</Link>
      <p>{[job.location, experience].filter(Boolean).join(" · ")}</p>
    </li>
  );
}

export default function JobDetail({
  job,
  relatedJobs = [],
  captchaSiteKey,
}: {
  job: PublicJob;
  relatedJobs?: PublicJob[];
  captchaSiteKey?: string;
}) {
  const [applying, setApplying] = React.useState(false);
  React.useEffect(() => {
    if (!applying) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setApplying(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [applying]);

  const experience = range(job.experience, " years");
  const posted = postedLabel(job.createdAt);
  const functionalArea = job.functionalArea?.secondary || job.functionalArea?.primary;
  const shareUrl = `https://allgoodjobs.in${jobUrl(job.title, job.id)}`;
  const safeDescription = descriptionText(job.description);

  return (
    <main className="container global-job-detail">
      <div className="global-job-detail__content">
        <section aria-labelledby="job-title">
          <article className="global-job-detail__card global-job-detail__summary">
            <div className="global-job-detail__summary-body">
              <h1 id="job-title">{job.title}</h1>
              <div className="global-job-detail__metadata">
                {experience ? <span><BriefcaseBusiness aria-hidden="true" />{experience}</span> : null}
                {job.location ? <span><MapPin aria-hidden="true" />{job.location}</span> : null}
              </div>
              <div className="global-job-detail__tags">
                {job.jobType ? <Badge variant="featured">{employmentLabel(job.jobType)}</Badge> : null}
                {functionalArea ? <Badge variant="secondary">{functionalArea}</Badge> : null}
              </div>
              <div className="global-job-detail__share" aria-label="Share this job">
                <span><Share2 aria-hidden="true" /> Share job</span>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" aria-label="Share this job on Facebook"><Facebook aria-hidden="true" /></a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" aria-label="Share this job on LinkedIn"><Linkedin aria-hidden="true" /></a>
              </div>
            </div>
            <footer>
              {posted ? <span><CalendarDays aria-hidden="true" />{posted}</span> : <span><Clock3 aria-hidden="true" />Public opportunity</span>}
              {job.openRoles !== null ? <span>{job.openRoles} opening{job.openRoles === 1 ? "" : "s"}</span> : null}
            </footer>
          </article>

          <article className="global-job-detail__card global-job-detail__description">
            <h2>Job Description</h2>
            {safeDescription ? <div className="job-description global-job-detail__description-copy">{safeDescription}</div> : <p className="job-description">No job description has been provided.</p>}
          </article>

        </section>

        <aside className="global-job-detail__sidebar" aria-label="Job actions and related jobs">
          <div className="global-job-detail__card global-job-detail__apply-card">
            <Button type="button" onClick={() => setApplying(true)}>Apply to Job</Button>
          </div>
          <section className="global-job-detail__card global-job-detail__related" aria-labelledby="related-jobs-heading">
            <h2 id="related-jobs-heading">Recent Jobs</h2>
            {relatedJobs.length ? <ul>{relatedJobs.map((relatedJob) => <RelatedJob key={relatedJob.id} job={relatedJob} />)}</ul> : <p>No other jobs to show right now.</p>}
          </section>
        </aside>
      </div>
      {applying ? (
        <div className="application-modal-backdrop" role="presentation" onMouseDown={() => setApplying(false)}>
          <section className="application-modal" role="dialog" aria-modal="true" aria-labelledby="application-title" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" className="application-modal__close" onClick={() => setApplying(false)} aria-label="Close application form"><X aria-hidden="true" /></button>
            <ApplicationForm jobId={job.id} captchaSiteKey={captchaSiteKey} />
          </section>
        </div>
      ) : null}
    </main>
  );
}
